import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import InsHeaderConnect from "components/InsHeaderConnect/InsHeaderConnect";
import Step1 from "./Step1/Step1";
import Step2 from "./Step2/Step2";
import Step3 from "./Step3/Step3";
import BrandsStep from "./BrandsStep/BrandsStep";
import AxesStep from "./AxesStep/AxesStep";
import { useDispatch } from "react-redux";
import { updateInfos } from "store/actions/createAccountAction";
import { createAccountAPI } from "../../../api/account";
import { getRetailerBrands, getRetailerAxes } from "../../../api/brands";
import { useTranslation, Trans } from "react-i18next";

const Create = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const accountCreationState = useSelector(
    (state) => state.createAccount.userInfo
  );
  const config = useSelector((state) => state.config);
  const [brandsData, setBrandsData] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [axesData, setAxesData] = useState([]);
  const [axesLoading, setAxesLoading] = useState(false);

  const [step, setstep] = useState(1);
  const [error, seterror] = useState("");
  const errorRef = useRef(null);

  const hasBrands = useMemo(
    () =>
      Array.isArray(config?.register?.brand) &&
      config.register.brand.length > 0,
    [config],
  );

  const hasAxes = useMemo(
    () => Array.isArray(config?.register?.preferenceAxis) && config.register.preferenceAxis.length > 0,
    [config]
  );
  
  const steps = useMemo(() => {
    const dynamicSteps = [
      { id: "step1", render: (props) => <Step1 {...props} /> },
      { id: "step2", render: (props) => <Step2 {...props} /> },
    ];
    if (hasBrands) {
      dynamicSteps.push({ id: "brands", render: (props) => <BrandsStep {...props} /> });
    }
    if (hasAxes) {
      dynamicSteps.push({ id: "axes", render: (props) => <AxesStep {...props} /> });
    }
    dynamicSteps.push({ id: "final", render: (props) => <Step3 {...props} /> });
    return dynamicSteps;
  }, [hasBrands, hasAxes]);

  const maxSteps = steps.length;
  useEffect(() => {
    if (step > maxSteps) {
      setstep(maxSteps);
    }
    if (step < 1) {
      setstep(1);
    }
  }, [maxSteps, step]);

  const createAccount = (data) => {
    const finalData = { ...accountCreationState, ...data };
    dispatch(updateInfos(finalData));
    seterror("");
    createAccountAPI(finalData).then(
      (data) => {
        //value a changer pour la bonne data
        let value = data.active;
        history.replace("/connect/create-success", {
          isCreated: value,
        });
      },
      (_error) => {
        console.log("erreur requete api : " + _error);
        if (_error === "some_brands_not_mapped_to_retailer") {
          setstep(3);
          seterror(t(_error));
          getBrands(accountCreationState?.retailer_code);
        }
        if (_error === "axes_no_longer_valid") {
          setstep(hasBrands ? 4 : 3);
          seterror(t(_error));
          getAxes(accountCreationState?.retailer_code);
        }
        if (_error?.flow && _error?.flow === "external" ) {
          let errorMessage = t(_error?.detail);
          if (_error?.email_in_use) {
            errorMessage = (
              <Trans
                i18nKey={_error?.detail}
                defaults={
                  "There is already an account existing with the email({{email}}), please reset your password (<link1>here</link1>) or reach to the hotline."
                }
                values={{ email: _error?.email_in_use }}
                components={{
                  link1: (
                    <a
                      className="underline_link"
                      href="/connect/forget-password"
                    />
                  ),
                }}
              />
            );
          }
          seterror(errorMessage);
        } else {
          seterror(t(_error));
        }
        const errorElement = errorRef?.current;
        errorElement?.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement?.focus();
      }
    );
  };

  const getBrands = useCallback((retailerCode) => {
    setBrandsLoading(true);
    getRetailerBrands({ retailer_code: retailerCode }).then(data => {
      setBrandsData(data?.brands || []);
    }).catch(() => {
      setBrandsData([]);
    }).finally(() => {
      setBrandsLoading(false);
    });
  }, []);

  const getAxes = useCallback((retailerCode) => {
    setAxesLoading(true);
    const payload = { brands: accountCreationState?.brands || [], retailer_code: retailerCode };
    getRetailerAxes(payload).then(data => {
      setAxesData(data?.axes || []);
    }).catch(() => {
      setAxesData([]);
    }).finally(() => {
      setAxesLoading(false);
    });
  }, [accountCreationState,brandsData]);

  const nextStep = (data) => {
    const locationState = {
      goBack: true,
      step: step + 1
    };
    const retailerCode = data?.retailer_code || accountCreationState?.retailer_code;
    if (
      step === 1 &&
      data?.retailer_code &&
      data?.retailer_code !== accountCreationState?.retailer_code
    ) {
      getBrands(retailerCode);
    }
    const axesStepIndex = steps.findIndex(s => s.id === "axes");
    const willEnterAxesStep = hasAxes && axesStepIndex !== -1 && step === axesStepIndex;
    if (hasAxes && willEnterAxesStep ) {
      getAxes(retailerCode);
    }
    setstep((state) => state + 1);
    seterror("");
    if (data) {
      dispatch(updateInfos(data));
    }
    history.push("/connect/create", locationState);
  };

  const goBack = () => {
    seterror("");
    setstep((state) => state - 1);
  };

  const currentIndex = Math.max(0, Math.min(step - 1, steps.length - 1));
  const Current = steps[currentIndex];

  const stepProps = {
    onValidate: currentIndex === steps.length - 1 ? createAccount : nextStep,
    onBack: goBack,
    currentStep: step,
    maxSteps: maxSteps,
    userInfo: accountCreationState,
    brands: brandsData,
    brandsLoading: brandsLoading,
    axes: axesData,
    axesLoading: axesLoading
  };

  return (
    <>
      <InsHeaderConnect title={t("create_account")} onGoBack={goBack} />
      <div ref={errorRef} className="step3-error-validate-account">{error}</div>
      {Current ? Current.render(stepProps) : null}
    </>
  );
};

export default Create;